// lib/aiDemoEnhancer.ts
// THIS FILE IS ONLY USED IN DEMO MODE
// It adds rich, realistic data to demo submissions without affecting real company data

import { deepseekApi } from './deepseekApi';
import { DemoCompany } from './demoCompanyService';

export interface EnhancedSubmission {
  [key: string]: any;
}

export const aiDemoEnhancer = {
  /**
   * Enhance a demo submission with rich, realistic data
   * This ONLY runs in demo mode and NEVER affects real submissions
   * Preserves all user input while adding demo-specific details
   */
  enhanceDemoSubmission: async (
    baseSubmission: any,
    company: DemoCompany | null
  ): Promise<EnhancedSubmission> => {
    try {
      // Check if we have an API key
      const hasApiKey = !!import.meta.env.VITE_DEEPSEEK_API_KEY;
      
      if (!hasApiKey) {
        console.log('No DeepSeek API key, using mock enhancements');
        return addMockEnhancements(baseSubmission, company);
      }

      // Prepare enhancement prompt
      const prompt = `
        Enhance this demo submission with rich, realistic details for a demo account.
        The user submitted basic information - add realistic supporting details while PRESERVING their core input.
        
        Company: ${company?.name || 'Demo Company'}
        Industry: ${company?.industry || 'manufacturing'}
        Problem Title: ${baseSubmission.problemTitle}
        
        User's Description: ${baseSubmission.plainDescription}
        
        Add realistic:
        - Root cause analysis (technical details)
        - Technical environment details (equipment, systems, age)
        - Market prevalence estimates (number of facilities affected)
        - Regulatory context (relevant certifications, bodies)
        - Self-assessment ratings (1-5 that make sense for the problem)
        - Supporting documents (2-3 realistic document names)
        
        Return ONLY the fields to add/enhance as a JSON object.
        Do NOT modify fields the user already filled in.
      `;

      // Get AI enhancements
      const enhancements = await deepseekApi.generateDemoSubmission(prompt);
      
      // Merge enhancements with base submission
      // Base submission data takes precedence (user input is never overwritten)
      const enhanced = {
        ...baseSubmission,
        ...enhancements,
        // Ensure arrays are properly merged
        evidence: [...new Set([...(baseSubmission.evidence || []), ...(enhancements.evidence || [])])],
        monitoringSystems: [...new Set([...(baseSubmission.monitoringSystems || []), ...(enhancements.monitoringSystems || [])])],
        certifications: [...new Set([...(baseSubmission.certifications || []), ...(enhancements.certifications || [])])],
      };

      return enhanced;
    } catch (error) {
      console.error('AI enhancement failed, using mock enhancements:', error);
      return addMockEnhancements(baseSubmission, company);
    }
  }
};

/**
 * Add mock enhancements when AI is unavailable
 * Still preserves user input while adding rich demo data
 */
function addMockEnhancements(baseSubmission: any, company: DemoCompany | null): EnhancedSubmission {
  const industry = company?.industry || 'manufacturing';
  const now = new Date().toISOString();
  
  // Industry-specific mock data
  const industryData: Record<string, any> = {
    'fmcg': {
      rootCause: 'Gradual wear in high-speed packaging equipment combined with inadequate predictive maintenance protocols',
      primaryEquipment: 'KHS Bottling Line, Tetra Pak Filling Machines',
      equipmentBrand: 'KHS GmbH / Tetra Pak',
      estimatedKenya: 25,
      estimatedEastAfrica: 75,
      certifications: ['ISO 9001', 'ISO 22000']
    },
    'agro_processing': {
      rootCause: 'Temperature fluctuations during processing due to aging refrigeration infrastructure and lack of real-time monitoring',
      primaryEquipment: 'Cooling towers, refrigeration units, pasteurizers',
      equipmentBrand: 'GEA / Alfa Laval',
      estimatedKenya: 30,
      estimatedEastAfrica: 90,
      certifications: ['HACCP', 'ISO 22000', 'KEBS']
    },
    'manufacturing': {
      rootCause: 'Undetected equipment degradation leading to unplanned downtime and quality variations',
      primaryEquipment: 'CNC machines, conveyor systems, industrial robots',
      equipmentBrand: 'Siemens / ABB / Fanuc',
      estimatedKenya: 20,
      estimatedEastAfrica: 60,
      certifications: ['ISO 9001', 'ISO 14001']
    },
    'default': {
      rootCause: 'Lack of real-time monitoring and predictive maintenance capabilities',
      primaryEquipment: 'Industrial machinery and processing equipment',
      equipmentBrand: 'Various manufacturers',
      estimatedKenya: 15,
      estimatedEastAfrica: 45,
      certifications: ['ISO 9001']
    }
  };

  const data = industryData[industry] || industryData.default;

  return {
    ...baseSubmission,
    
    // Add rich demo data only for fields the user didn't fill
    ...(!baseSubmission.rootCause && { rootCause: data.rootCause }),
    ...(!baseSubmission.primaryEquipment && { primaryEquipment: data.primaryEquipment }),
    ...(!baseSubmission.equipmentBrand && { equipmentBrand: data.equipmentBrand }),
    ...(!baseSubmission.estimatedKenya && { estimatedKenya: data.estimatedKenya }),
    ...(!baseSubmission.estimatedEastAfrica && { estimatedEastAfrica: data.estimatedEastAfrica }),
    
    // Add technical environment details if missing
    ...(!baseSubmission.facilityAge && { facilityAge: company?.facilityAge || '25' }),
    ...(!baseSubmission.facilitySize && { facilitySize: company?.facilitySize || '85000' }),
    ...(!baseSubmission.operatingHours && { operatingHours: company?.operatingHours || '24/7' }),
    ...(!baseSubmission.operatingDays && { operatingDays: company?.operatingDays || '350' }),
    
    // Add monitoring systems if missing
    ...(!baseSubmission.monitoringSystems && { 
      monitoringSystems: company?.monitoringSystems || ['SCADA', 'ERP', 'Manual records'] 
    }),
    
    // Add certifications if missing
    ...(!baseSubmission.certifications && { 
      certifications: company?.certifications || data.certifications 
    }),
    
    // Add self-assessment ratings if missing (make them realistic)
    ...(!baseSubmission.severityRating && { severityRating: '4' }),
    ...(!baseSubmission.urgencyRating && { urgencyRating: '4' }),
    ...(!baseSubmission.prevalenceRating && { prevalenceRating: '3' }),
    ...(!baseSubmission.dataAvailabilityRating && { dataAvailabilityRating: '3' }),
    ...(!baseSubmission.techOpennessRating && { techOpennessRating: '4' }),
    ...(!baseSubmission.managementSupportRating && { managementSupportRating: '4' }),
    ...(!baseSubmission.decisionSpeedRating && { decisionSpeedRating: '3' }),
    
    // Add demo documents
    documents: baseSubmission.documents?.length ? baseSubmission.documents : [
      { 
        name: `${baseSubmission.problemTitle?.toLowerCase().replace(/\s+/g, '_')}_logs.xlsx`, 
        url: '#', 
        size: '2.4 MB', 
        type: 'Excel' 
      },
      { 
        name: 'maintenance_records_2025.pdf', 
        url: '#', 
        size: '1.8 MB', 
        type: 'PDF' 
      },
      { 
        name: 'equipment_specifications.pdf', 
        url: '#', 
        size: '3.2 MB', 
        type: 'PDF' 
      }
    ],
    
    // Add metadata
    enhanced: true,
    enhancedAt: now,
    enhancementSource: 'mock'
  };
}